import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MentionPickerButton from "@/components/common/MentionPickerButton";
import { createMentionSuggestion } from "@/components/common/MentionSuggestionList";
import {
  Bold, Italic, Strikethrough, List, ListOrdered, Heading2, Heading3,
  Quote, Code, Link2, Undo, Redo, Minus, Smile,
} from "lucide-react";

// iter155 · Link-Mark erweitern damit `data-mention` das ProseMirror-Roundtripping überlebt.
// Ohne diese Extension strippt Tiptap unbekannte Attribute → `@DisplayName`-Text bleibt zwar,
// aber der Anchor-Wrapper geht verloren (siehe DB-Post 9f2d6816).
const MentionLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      "data-mention": {
        default: null,
        parseHTML: (el) => el.getAttribute("data-mention"),
        renderHTML: (attrs) => (attrs["data-mention"] ? { "data-mention": attrs["data-mention"] } : {}),
      },
      class: {
        default: null,
        parseHTML: (el) => el.getAttribute("class"),
        renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {}),
      },
    };
  },
});

// iter157 · Inline @-Mention-Extension. Rendert `<a class="mention-link" href="/user/{id}" data-mention="{id}">@{label}</a>`.
// Identisch zum Toolbar-Button-Insert → RichTextView + Backend behandeln beides gleich.
const InlineMention = Mention.extend({
  parseHTML() {
    return [{ tag: "a[data-mention]" }];
  },
  renderHTML({ node }) {
    const id = node.attrs.id;
    const label = node.attrs.label || id;
    return [
      "a",
      {
        href: `/user/${id}`,
        "data-mention": id,
        class: "mention-link",
      },
      `@${label}`,
    ];
  },
  renderText({ node }) {
    return `@${node.attrs.label || node.attrs.id}`;
  },
});

// Emoji-Kategorien für den Toolbar-Picker (WhatsApp-Style)
const EMOJI_CATEGORIES = [
  { key: "smileys", label: "Smileys", emojis: ["😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃","😉","😊","😇","🥰","😍","🤩","😘","😗","😚","😙","😋","😛","😜","🤪","😝","🤑","🤗","🤭","🤫","🤔","🤨","😐","😑","😶","😏","😒","🙄","😬","🤥","😌","😔","😪","🤤","😴","😷","🤒","🤢","🤮","🥵","🥶","🥴","😵","🤯","🤠","🥳","😎","🤓","🧐","😕","😟","🙁","☹️","😮","😯","😲","😳","🥺","😦","😧","😨","😰","😥","😢","😭","😱","😖","😣","😞","😓","😩","😫","🥱","😤","😡","😠","🤬","😈","👿"] },
  { key: "hearts", label: "Herzen", emojis: ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❣️","💕","💞","💓","💗","💖","💘","💝","💟"] },
  { key: "intim", label: "Intim", emojis: ["💋","👅","💦","🔥","🍑","🍆","💯","👀","😻","🌶️","🥵","🍓","🍯","💎","✨","🌸","🌺","🌷","🌹","💐","🦋"] },
  { key: "hands", label: "Hände", emojis: ["👋","🤚","✋","🖖","👌","🤌","🤏","✌️","🤞","🤟","🤘","🤙","👈","👉","👆","🖕","👇","☝️","👍","👎","👊","✊","🤛","🤜","👏","🙌","👐","🤲","🤝","🙏","💅","🤳","💪","🦾","👀","👁️"] },
  { key: "objects", label: "Objekte", emojis: ["🛍️","🎁","💌","📦","💳","💰","🏷️","🎀","💄","👗","👙","👘","🩱","🩲","🩳","👕","👚","🧥","👖","🧣","🧤","🧦","👞","👟","👠","👡","👢","🥿","👒","🎩","🧢","👑","💎","💍","🔑","🔒","🔓"] },
  { key: "symbols", label: "Symbole", emojis: ["✅","❌","⚠️","🚫","💯","💢","💥","💫","⭐","🌟","🎉","🎊","🎈","🍾","🥂","🍷","🍻","🍺","🥃","☕","🍵","🧋","🥤","🧉","🍹"] },
];

/**
 * RichTextEditor — TipTap-basierter WYSIWYG-Editor (HTML-Output).
 *
 * Props:
 *  - value:       aktueller HTML-String
 *  - onChange:    (html: string) => void
 *  - placeholder: Anzeigetext bei leerem Editor
 *  - minHeight:   optional CSS-Höhe (default 240px)
 *  - testId:      data-testid Prefix
 */
export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Text hier eingeben …",
  minHeight = "240px",
  testId = "rich-text-editor",
}) {
  const { t } = useTranslation();
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiTab, setEmojiTab] = useState("smileys");
  const emojiWrapRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        link: false, // iter156 · StarterKit ab v3 enthält Link. Wir deaktivieren die
                     // Default-Instanz zu Gunsten unserer MentionLink-Extension (unten).
      }),
      // iter156 · MentionLink erweitert Link um `data-mention` + `class` und trägt einen
      // eindeutigen Namen, damit es keine „Duplicate extension names"-Warning gibt.
      MentionLink.configure({
        openOnClick: false,
        autolink: true,
        validate: (href) => !!href,
      }),
      InlineMention.configure({
        HTMLAttributes: { class: "mention-link" },
        renderText: ({ node }) => `@${node.attrs.label || node.attrs.id}`,
        suggestion: createMentionSuggestion(),
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      if (typeof onChange === "function") onChange(html);
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none px-3 py-3 min-h-[200px]",
        "data-testid": `${testId}-content`,
        style: `min-height:${minHeight}`,
      },
      // iter156 · Verhindert dass Klick auf einen Mention-Link IM Editor den Browser-Tab öffnet.
      // Der Link soll erst im veröffentlichten Beitrag aktiv sein.
      handleDOMEvents: {
        click: (_view, event) => {
          const t = event.target;
          const a = t && t.closest ? t.closest("a[href]") : null;
          if (a) {
            event.preventDefault();
            event.stopPropagation();
          }
          return false;
        },
      },
    },
  });

  // Externer value-Wechsel (z. B. Template-Auswahl) → Editor-Content überschreiben
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  // Emoji-Popover: bei Klick außerhalb schließen
  useEffect(() => {
    if (!emojiOpen) return;
    const handler = (e) => {
      if (emojiWrapRef.current && !emojiWrapRef.current.contains(e.target)) {
        setEmojiOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [emojiOpen]);

  if (!editor) return null;

  const btn = (active) =>
    `h-8 w-8 flex items-center justify-center rounded transition-colors ${
      active ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:bg-[#F5F5F5]"
    }`;

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div data-testid={testId} className="border border-[#E5E5E5] bg-white">
      <div className="flex items-center gap-1 border-b border-[#E5E5E5] px-2 py-1.5 flex-wrap">
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={btn(editor.isActive("heading", { level: 2 }))} data-testid={`${testId}-h2`} title={t("rich_text.h2")}>
          <Heading2 size={15} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={btn(editor.isActive("heading", { level: 3 }))} data-testid={`${testId}-h3`} title={t("rich_text.h3")}>
          <Heading3 size={15} />
        </button>
        <div className="w-px h-5 bg-[#E5E5E5] mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()}
          className={btn(editor.isActive("bold"))} data-testid={`${testId}-bold`} title="Fett">
          <Bold size={15} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()}
          className={btn(editor.isActive("italic"))} data-testid={`${testId}-italic`} title="Kursiv">
          <Italic size={15} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()}
          className={btn(editor.isActive("strike"))} data-testid={`${testId}-strike`} title="Durchgestrichen">
          <Strikethrough size={15} />
        </button>
        <div className="w-px h-5 bg-[#E5E5E5] mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={btn(editor.isActive("bulletList"))} data-testid={`${testId}-ul`} title={t("rich_text.bullet")}>
          <List size={15} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={btn(editor.isActive("orderedList"))} data-testid={`${testId}-ol`} title="Nummerierte Liste">
          <ListOrdered size={15} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={btn(editor.isActive("blockquote"))} data-testid={`${testId}-quote`} title="Zitat">
          <Quote size={15} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleCode().run()}
          className={btn(editor.isActive("code"))} data-testid={`${testId}-code`} title="Inline-Code">
          <Code size={15} />
        </button>
        <div className="w-px h-5 bg-[#E5E5E5] mx-1" />
        <button type="button" onClick={promptLink}
          className={btn(editor.isActive("link"))} data-testid={`${testId}-link`} title={t("rich_text.link")}>
          <Link2 size={15} />
        </button>
        <MentionPickerButton
          testId={`${testId}-mention`}
          onSelect={({ handle, display_name }) => {
            const label = (display_name || handle || "").trim();
            // iter155 · JSON-Insert statt HTML-String → Text und Mark-Attrs überleben garantiert.
            editor
              .chain()
              .focus()
              .insertContent([
                {
                  type: "text",
                  text: `@${label}`,
                  marks: [
                    {
                      type: "link",
                      attrs: {
                        href: `/user/${handle}`,
                        "data-mention": handle,
                        class: "mention-link",
                        target: null,
                        rel: null,
                      },
                    },
                  ],
                },
                { type: "text", text: "\u00A0" },
              ])
              .run();
          }}
        />
        <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className={btn(false)} data-testid={`${testId}-hr`} title="Trennlinie">
          <Minus size={15} />
        </button>
        <div className="w-px h-5 bg-[#E5E5E5] mx-1 hidden sm:block" />
        {/* Emoji-Picker inline im Toolbar-Kontext (nicht mehr über Container-Rand hinaus positioniert).
            iter · Auf Mobil ausgeblendet — die Geräte-Tastatur bringt eigene Emojis mit. */}
        <div className="relative hidden sm:block" ref={emojiWrapRef}>
          <button
            type="button"
            onClick={() => setEmojiOpen((v) => !v)}
            className={btn(emojiOpen)}
            data-testid={`${testId}-emoji`}
            title={t("emoji_picker.title", { defaultValue: "Emoji" })}
          >
            <Smile size={15} />
          </button>
          {emojiOpen && (
            <div
              data-testid={`${testId}-emoji-panel`}
              className="absolute z-50 mt-1 left-0 w-80 max-w-[calc(100vw-1rem)] bg-white border border-[#0A0A0A] shadow-xl"
            >
              <div className="flex overflow-x-auto border-b border-[#E5E5E5] bg-[#FAFAFA]">
                {EMOJI_CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setEmojiTab(c.key)}
                    className={`px-2.5 py-1.5 text-[10px] whitespace-nowrap border-b-2 shrink-0 ${
                      emojiTab === c.key
                        ? "border-[#0A0A0A] text-[#0A0A0A]"
                        : "border-transparent text-[#999] hover:text-[#0A0A0A]"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-9 gap-0.5 p-2 max-h-44 overflow-y-auto">
                {(EMOJI_CATEGORIES.find((c) => c.key === emojiTab)?.emojis || []).map((emoji, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      editor.chain().focus().insertContent(emoji).run();
                      setEmojiOpen(false);
                    }}
                    className="h-7 w-7 text-base hover:bg-[#F0F0F0] flex items-center justify-center"
                    title={emoji}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="w-px h-5 bg-[#E5E5E5] mx-1" />
        <button type="button" onClick={() => editor.chain().focus().undo().run()}
          className={btn(false)} data-testid={`${testId}-undo`} title={t("rich_text.undo")}>
          <Undo size={15} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()}
          className={btn(false)} data-testid={`${testId}-redo`} title="Wiederholen">
          <Redo size={15} />
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
